/**
 * ImageWidget — catalog entry for contentType 'image'.
 *
 * Local-first upload: the picked file is hashed and written to OPFS
 * (opfsAssetStore) with NO network call, and the grid item stores only its
 * content address `{ sha256, mime, ext }`. The bytes sync to the backend
 * later, at publish time (publishSite POSTs not-yet-synced assets) — same
 * model as the Yjs doc, which lives in OPFS and only syncs on publish.
 *
 * Display resolves a src in this order: a legacy backend `url` (older items)
 * → the OPFS bytes as an object URL → a backend fallback `/api/v1/assets/...`
 * when the asset isn't in this device's OPFS (e.g. a doc opened elsewhere).
 *
 * Alt text is stored in `contentProps.alt`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css, html } from "react-strict-dom";
import { FileTrigger, Tooltip } from "@duro-app/ui";
import type { FileTriggerHandle } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { useGridStore, useUpdateGrid } from "@fredericrous/grid";
import {
  DEFAULT_PLACEHOLDER_PALETTE,
  placeholderArt,
} from "@website-builder/theme-engine";
import type { WidgetProps } from "./types.js";
import { useThemeVars } from "../ThemeVarsContext.js";
import { usePageEdit } from "../PageEditContext.js";
import { useSiteId } from "../SiteContext.js";
import { getAssetFile, putAsset } from "../assets/opfsAssetStore.js";
import {
  getCollabBlobResolver,
  getCollabBlobAnnounce,
} from "../assets/collabBlobResolver.js";
import {
  markBackingUp,
  useBackupStatus,
} from "../assets/collabBackupStatus.js";
import { useRoomPeerCount } from "../assets/collabPeers.js";
import { openMediaLibraryForPick } from "../mediaLibrary.js";
import { useAddDrag } from "../AddDragContext.js";
import { useMediaBindRef } from "./mediaBindRegistry.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { effects } from "@duro-app/tokens/tokens/effects.css";

const ACCEPTED = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";

// Editor chrome (not published): dark pills over the image, the accent ring.
// rgb()/rem like SectionWidget — the editor package compiles no Duro token
// imports yet (no deep-import babel pass in its vitest/vite/metro pipelines).
const CHROME = "rgba(20, 22, 28, 0.72)";
const ON_CHROME = "rgb(255, 255, 255)";
const ACCENT = "rgb(37, 99, 235)";
const ACCENT_SOFT = "rgb(110, 168, 254)";
const TEXT = "rgb(55, 65, 81)";
const TEXT_MUTED = "rgb(107, 114, 128)";
const LINK = "rgb(59, 130, 246)";
const ERROR = "rgb(220, 38, 38)";

const styles = css.create({
  // ── The tile ──
  container: {
    width: "100%",
    height: "100%",
    minHeight: sizes.placeholderMinH,
    position: "relative",
    borderRadius: "0.25rem",
    borderWidth: 0,
    borderStyle: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    outlineOffset: borders.focusOffset,
  },
  containerCropping: (grabbing: boolean) => ({
    cursor: grabbing ? "grabbing" : "grab",
    outlineWidth: borders.focusRing,
    outlineStyle: "dashed",
    outlineColor: ACCENT_SOFT,
    touchAction: "none",
  }),
  containerIdle: {
    cursor: "pointer",
  },
  // A dragged media asset hovering an EMPTY tile: the drop-to-fill ring.
  bindTarget: {
    backgroundColor: colors.infoBg,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: ACCENT,
  },
  filled: {
    backgroundColor: "transparent",
  },
  // Placeholder art (an empty slot): longhands, not the `background`
  // shorthand — jsdom's CSSOM drops a shorthand carrying a data: URL; as a
  // style function the URI rides in the inline style, where the tests read it.
  art: (uri: string) => ({
    backgroundImage: `url("${uri}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  }),
  // Mockup (far-zoom / preview) tile: the art alone, what the page publishes.
  mockupArt: {
    width: "100%",
    height: "100%",
    minHeight: sizes.placeholderMinH,
    borderRadius: "0.25rem",
  },
  // ── The image ──
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imageFrame: (position: string, scale: number) => ({
    objectPosition: position,
    transform: scale !== 1 ? `scale(${scale})` : "none",
    transformOrigin: position,
  }),
  // The container owns the pan gesture; a bare <img> would start a native
  // image-drag and swallow the pointer.
  imageInert: {
    pointerEvents: "none",
    userSelect: "none",
  },
  // ── Crop mode ──
  thirds: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)," +
      "linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
    backgroundSize: "100% 33.333%, 33.333% 100%",
    backgroundPosition: "0 33.333%, 33.333% 0",
    mixBlendMode: "difference",
  },
  pill: {
    backgroundColor: CHROME,
    color: ON_CHROME,
    fontSize: "0.68rem",
    fontWeight: typography.fontWeightSemibold,
    borderRadius: radii.full,
    borderWidth: 0,
    borderStyle: "none",
  },
  replaceSlot: {
    position: "absolute",
    top: "0.25rem",
    right: "0.25rem",
  },
  zoomBar: {
    position: "absolute",
    bottom: "0.5rem",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    paddingTop: microSpacing.px5,
    paddingBottom: microSpacing.px5,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
  },
  zoomSlider: {
    width: sizes.sliderW,
    cursor: "pointer",
    accentColor: ACCENT_SOFT,
  },
  zoomValue: {
    width: sizes.readoutW,
    textAlign: "right",
  },
  // ── Empty state ──
  // The upload affordance floats over the placeholder art in a translucent
  // pill so it reads on any brand colour; the art itself is the picture the
  // published page shows (no kind-icon needed).
  uploadPrompt: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.25rem",
    color: TEXT,
    fontSize: "0.8rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    borderRadius: radii.full,
    backgroundColor: colors.overlayLight,
    backdropFilter: effects.surfaceBlur,
    boxShadow: shadows.sm,
    maxWidth: `calc(100% - ${spacing.md})`,
    boxSizing: "border-box",
  },
  libraryLink: {
    backgroundColor: "transparent",
    borderWidth: 0,
    borderStyle: "none",
    padding: 0,
    color: LINK,
    fontSize: typography.fontSizeXs,
    fontWeight: typography.fontWeightSemibold,
    cursor: "pointer",
    textDecorationLine: "underline",
  },
  uploading: {
    color: TEXT_MUTED,
    fontSize: "0.8rem",
  },
  backupSlot: {
    position: "absolute",
    top: "0.25rem",
    left: "0.25rem",
  },
  backupBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    paddingTop: microSpacing.px2,
    paddingBottom: microSpacing.px2,
    paddingLeft: microSpacing.px6,
    paddingRight: microSpacing.px6,
  },
  error: {
    color: ERROR,
    fontSize: typography.fontSizeXs,
    position: "absolute",
    bottom: "0.25rem",
    left: "0.25rem",
    right: "0.25rem",
  },
  // Mounts the file picker outside crop mode, where the tile's own press
  // calls the picker's open().
  pickerHidden: {
    display: "none",
  },
});

export function ImageWidget({ itemId, contentProps, gridId }: WidgetProps) {
  const url = contentProps?.["url"] as string | undefined;
  const sha256 = contentProps?.["sha256"] as string | undefined;
  const ext = contentProps?.["ext"] as string | undefined;
  const mime = contentProps?.["mime"] as string | undefined;
  const alt = contentProps?.["alt"] as string | undefined;
  const siteId = useSiteId();
  const preview = contentProps?.["preview"] as boolean | undefined;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Seed from the legacy url so url-only items render on first paint; OPFS
  // resolution (async) fills it in for content-addressed items below.
  const [src, setSrc] = useState<string | undefined>(
    typeof url === "string" && url ? url : undefined,
  );
  const pickerRef = useRef<FileTriggerHandle>(null);
  const { updateItem } = useUpdateGrid(gridId);
  // Truly-empty test uses sha256/url (persistent), NOT `src` (async display state
  // — a filled item can briefly lack src while it resolves). Only an empty item
  // advertises itself as a drop-to-fill target; the ring shows while it's hovered.
  const empty = !sha256 && !url;

  // On-brand placeholder art for an empty slot — the same picture the
  // published page draws (renderPage) so the canvas stays WYSIWYG: seeded by
  // the item id, coloured from the site's --wb-* Brand vars (a default
  // blue-grey when the canvas is unthemed).
  const themeVars = useThemeVars();
  const artPrimary = themeVars["--wb-color-primary"];
  const artAccent = themeVars["--wb-color-accent"];
  const art = useMemo(
    () =>
      placeholderArt(
        artPrimary && artAccent
          ? { primary: artPrimary, accent: artAccent }
          : DEFAULT_PLACEHOLDER_PALETTE,
        itemId,
      ),
    [artPrimary, artAccent, itemId],
  );
  const isBindTarget = useAddDrag().bindTargetId === itemId;
  // Select→drill-in (grid 0.8.40): under select-first a click on a FILLED image
  // that isn't yet selected only selects the tile (so it can be moved). Drilling
  // in (click an already-selected image) enters CROP mode — drag the image to
  // reframe what the tile shows (object-position), Replace swaps the file. The
  // first click on an EMPTY placeholder uploads. Legacy "press" mode keeps the
  // old always-open-picker behaviour. `selected` is the render-time value
  // (before this click's select), so a first click reads false.
  const selectFirst =
    useGridStore((s) => s.grids[gridId]?.config.activation) === "select-first";
  const selected = useGridStore(
    (s) =>
      s.selection?.gridId === gridId && s.selection.itemIds.includes(itemId),
  );
  // Free-mode P2P: "backing-up" until a peer has a replica (D1). Undefined solo.
  const backup = useBackupStatus(sha256);
  const roomPeers = useRoomPeerCount(); // peers online to replicate to (R7)

  // ── Crop-by-pan (Phase 4) ───────────────────────────────────────────────────
  // The tile shows the image object-fit:cover; `objectPosition` (persisted, %
  // per axis) chooses WHICH part is visible. In crop mode the user drags the
  // image to move that window; a rule-of-thirds overlay guides the framing.
  const objectPosition =
    (contentProps?.["objectPosition"] as string | undefined) ?? "50% 50%";
  // Zoom WITHIN the tile: object-fit cover is the 1× baseline (image exactly
  // fills the frame); `scale` (≥1, persisted) zooms further in, the tile crops
  // the overflow. Anchored on the framed focal point (transform-origin === the
  // pan position) so zooming keeps the subject put. 1× floor prevents gaps.
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const scale = (contentProps?.["scale"] as number | undefined) ?? 1;
  const { editingItemId, setEditingItemId } = usePageEdit();
  const cropping = selectFirst && editingItemId === itemId && !!src;
  const containerRef = useRef<HTMLDivElement>(null);
  const keepContainer = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
  }, []);
  const tileRef = useMediaBindRef<HTMLDivElement>(
    "image",
    gridId,
    itemId,
    empty,
    keepContainer,
  );
  // Live position during a drag (not yet persisted) so the pan is 60fps and one
  // undo step lands on pointer-up, not per frame.
  const [livePos, setLivePos] = useState<string | null>(null);
  const [liveScale, setLiveScale] = useState<number | null>(null);
  const shownPos = livePos ?? objectPosition;
  const shownScale = liveScale ?? scale;
  const panRef = useRef<{
    x: number;
    y: number;
    px: number;
    py: number;
  } | null>(null);

  // Leaving the selection (click away / another tile drills in) exits crop.
  useEffect(() => {
    if (cropping && !selected) setEditingItemId(null);
  }, [cropping, selected, setEditingItemId]);

  const parsePos = (s: string): { x: number; y: number } => {
    const [x, y] = s.split(/\s+/).map((v) => parseFloat(v));
    return { x: Number.isFinite(x) ? x! : 50, y: Number.isFinite(y) ? y! : 50 };
  };
  const clamp = (v: number) => Math.min(100, Math.max(0, v));

  const onPanDown = useCallback(
    (e: React.PointerEvent) => {
      if (!cropping) return;
      // Own the gesture: a drag here reframes the image, it does NOT move the
      // grid tile (that's a drag on the selected-but-not-cropping tile).
      e.stopPropagation();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const p = parsePos(shownPos);
      panRef.current = { x: e.clientX, y: e.clientY, px: p.x, py: p.y };
    },
    [cropping, shownPos],
  );
  const onPanMove = useCallback(
    (e: React.PointerEvent) => {
      const start = panRef.current;
      if (!start) return;
      const rect = containerRef.current?.getBoundingClientRect();
      const w = rect?.width || 1;
      const h = rect?.height || 1;
      // Drag the image right → reveal its left edge → object-position X drops.
      // Scale travel by tile size so a full-tile drag pans ~100% (inverse map);
      // divide by the zoom so the image tracks the cursor 1:1 at any zoom.
      const nx = clamp(
        start.px - ((e.clientX - start.x) / w / shownScale) * 100,
      );
      const ny = clamp(
        start.py - ((e.clientY - start.y) / h / shownScale) * 100,
      );
      setLivePos(`${nx.toFixed(1)}% ${ny.toFixed(1)}%`);
    },
    [shownScale],
  );
  const onPanUp = useCallback(() => {
    const start = panRef.current;
    panRef.current = null;
    if (start && livePos && livePos !== objectPosition) {
      updateItem(itemId, {
        contentProps: { ...(contentProps ?? {}), objectPosition: livePos },
      });
    }
    setLivePos(null);
  }, [livePos, objectPosition, itemId, contentProps, updateItem]);
  const onCropKeyDown = useCallback(
    // RSD types the keydown event minimally (no stopPropagation on the type);
    // the runtime event is a real KeyboardEvent (as TextEditorWidget).
    (e: { key: string }) => {
      if (cropping && e.key === "Escape") {
        (e as { stopPropagation?: () => void }).stopPropagation?.();
        setEditingItemId(null);
      }
    },
    [cropping, setEditingItemId],
  );
  // Zoom: persist the final scale as ONE undo step. Drop `scale` back out of
  // contentProps when it returns to the 1× baseline so a never-zoomed image
  // stays clean.
  const scaleCommit = useRef<number | null>(null);
  const commitScale = useCallback(() => {
    const s = scaleCommit.current;
    scaleCommit.current = null;
    if (s == null) return;
    const next: Record<string, unknown> = { ...(contentProps ?? {}) };
    if (Math.abs(s - 1) < 0.001) delete next["scale"];
    else next["scale"] = s;
    updateItem(itemId, { contentProps: next });
    setLiveScale(null);
  }, [contentProps, itemId, updateItem]);
  const nudgeZoom = useCallback(
    (delta: number) => {
      const base = scaleCommit.current ?? liveScale ?? scale;
      const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, base + delta));
      scaleCommit.current = s;
      setLiveScale(s);
    },
    [liveScale, scale],
  );
  // Wheel over a cropping tile zooms the IMAGE (not the canvas). A native
  // non-passive listener is required to preventDefault the page/canvas zoom;
  // React's onWheel is passive. Committed after the gesture idles.
  const wheelIdle = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!cropping || !el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      nudgeZoom(-e.deltaY * 0.0015);
      if (wheelIdle.current) clearTimeout(wheelIdle.current);
      wheelIdle.current = setTimeout(commitScale, 160);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [cropping, nudgeZoom, commitScale]);
  // Click routing when NOT cropping: empty/legacy → upload, filled + already
  // selected → enter crop, unselected filled → let the grid select this press.
  //
  // The decision runs on a WINDOW pointerup, NOT this element's onClick/onPointerUp
  // — the press bubbles to the GridItem box, which arms its press-or-drag gesture
  // and RETARGETS the release to the section block (with pointer capture), so a
  // `click`/element-`pointerup` on this widget never fires and the second click
  // "does nothing" (the drill-in bug the user hit; text drilling solved it the
  // same way — TextEditorWidget). `wasSelected` is read at pointerdown, BEFORE the
  // grid's select-on-press flushes, so a FIRST click reads false and only selects.
  const onSurfaceDown = useCallback(
    (e: React.PointerEvent) => {
      if (uploading) return;
      const sel = useGridStore.getState().selection;
      const wasSelected =
        !!sel && sel.gridId === gridId && sel.itemIds.includes(itemId);
      const sx = e.clientX;
      const sy = e.clientY;
      const finish = (up: PointerEvent) => {
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", cancel);
        // Match the grid's drag-arm threshold (8px): a farther press was a drag
        // to move the tile, not a click.
        if (Math.hypot(up.clientX - sx, up.clientY - sy) > 8) return;
        if (!selectFirst || !src) {
          pickerRef.current?.open(); // empty/legacy → open the file picker
          return;
        }
        if (wasSelected) setEditingItemId(itemId); // drill into crop
      };
      const cancel = () => {
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", cancel);
      };
      window.addEventListener("pointerup", finish);
      window.addEventListener("pointercancel", cancel);
    },
    [uploading, selectFirst, src, gridId, itemId, setEditingItemId],
  );

  // Resolve the displayed src from whatever the item carries.
  useEffect(() => {
    if (typeof url === "string" && url) {
      setSrc(url);
      return;
    }
    if (!sha256) {
      setSrc(undefined);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      const show = (file: Blob): void => {
        // The OPFS File has no mime; retype it with a lazy slice (no copy)
        // so <img> knows it's an image. The object URL references the bytes
        // on disk — they aren't read into memory here.
        const typed = mime ? file.slice(0, file.size, mime) : file;
        objectUrl = URL.createObjectURL(typed);
        setSrc(objectUrl);
      };

      const file = siteId
        ? await getAssetFile(siteId, sha256, ext ?? "bin")
        : null;
      if (cancelled) return;
      if (file) {
        show(file);
        return;
      }

      // Not in this device's OPFS (e.g. an image a collaborator added). In
      // free-mode collaboration, ask a peer for it FIRST (P2P, no cloud): the
      // blob exchange sha-verifies it and writes it into the same OPFS bucket,
      // so we just re-read OPFS on success.
      const resolver = siteId ? getCollabBlobResolver(siteId) : undefined;
      if (resolver) {
        const bytes = await resolver(sha256).catch(() => null);
        if (cancelled) return;
        if (bytes) {
          const peerFile = await getAssetFile(siteId!, sha256, ext ?? "bin");
          if (cancelled) return;
          if (peerFile) {
            show(peerFile);
            return;
          }
        }
      }

      // Last resort: the backend asset route — collaborator-readable, served
      // from the site's object store once the bytes have been published.
      setSrc(siteId ? `/api/v1/assets/${siteId}/${sha256}` : undefined);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, sha256, ext, mime, siteId]);

  const handleFile = async (file: File | undefined) => {
    if (!file || !siteId) return;

    setUploading(true);
    setError(null);
    try {
      const asset = await putAsset(siteId, file);
      // Persist the content address (not a URL) — drop any legacy `url` so
      // display resolves from OPFS. publishSite uploads the bytes later.
      const next: Record<string, unknown> = { ...(contentProps ?? {}) };
      delete next["url"];
      next["sha256"] = asset.sha256;
      next["mime"] = asset.mime;
      next["ext"] = asset.ext;
      updateItem(itemId, { contentProps: next });
      // Free-mode collab: announce the new image so peers eagerly replicate it,
      // and flag it "backing up" until a peer takes a copy (D1). No-op solo.
      const announce = siteId ? getCollabBlobAnnounce(siteId) : undefined;
      if (announce) {
        announce(asset.sha256);
        markBackingUp(asset.sha256);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  // The file picker: the visible Replace button in crop mode; otherwise it
  // stays mounted (hidden) so a press on the tile can open() it.
  const picker = (
    <FileTrigger
      ref={pickerRef}
      accept={ACCEPTED}
      variant="secondary"
      size="small"
      onSelect={(files) => void handleFile(files[0])}
    >
      Replace
    </FileTrigger>
  );

  if (preview) {
    return src ? (
      <html.img
        src={src}
        alt={alt ?? ""}
        style={[styles.image, styles.imageFrame(objectPosition, scale)]}
      />
    ) : (
      // Mockup mode: the placeholder art alone — what the page publishes.
      <html.div aria-hidden style={[styles.mockupArt, styles.art(art)]} />
    );
  }

  const surface = isBindTarget
    ? styles.bindTarget
    : src
      ? styles.filled
      : styles.art(art);

  return (
    <html.div
      // Empty widgets are drop-to-fill targets for a dragged image asset; the
      // same element backs the non-passive wheel listener (containerRef).
      ref={tileRef}
      tabIndex={cropping ? 0 : undefined}
      style={[
        styles.container,
        cropping
          ? styles.containerCropping(panRef.current !== null)
          : styles.containerIdle,
        surface,
      ]}
      onPointerDown={cropping ? onPanDown : onSurfaceDown}
      onPointerMove={cropping ? onPanMove : undefined}
      onPointerUp={cropping ? onPanUp : undefined}
      onKeyDown={onCropKeyDown}
    >
      {src && (
        <html.img
          src={src}
          alt={alt ?? ""}
          draggable={false}
          style={[
            styles.image,
            styles.imageFrame(shownPos, shownScale),
            styles.imageInert,
          ]}
        />
      )}
      {/* Rule-of-thirds overlay + Replace affordance, crop mode only. */}
      {cropping && (
        <>
          <html.div aria-hidden style={styles.thirds} />
          <html.span
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            style={styles.replaceSlot}
          >
            {picker}
          </html.span>
          {/* Zoom control — a slider is the discoverable, precise, touch-friendly
              affordance; scroll/pinch is the accelerator. Kept OFF the tile's own
              resize handles so "resize the frame" and "resize the image" never
              collide. Pointer events are trapped so sliding doesn't pan/move. */}
          <html.div
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={[styles.pill, styles.zoomBar]}
          >
            <html.span aria-hidden>🔍</html.span>
            {/* holds-until: Duro ships a Slider (https://github.com/fredericrous/duro-design-system/issues/105) */}
            {/* eslint-disable-next-line duro/no-raw-html-element -- an editor zoom slider, not a form field: Duro has no Slider (`npx duro list`), and the form kit's Field/Input carry label + validation chrome this crop control must not */}
            <input
              type="range"
              min={MIN_SCALE}
              max={MAX_SCALE}
              step={0.01}
              value={shownScale}
              aria-label="Zoom image"
              onChange={(e) => {
                scaleCommit.current = Number(e.target.value);
                setLiveScale(Number(e.target.value));
              }}
              onPointerUp={commitScale}
              onKeyUp={commitScale}
              {...css.props(styles.zoomSlider)}
            />
            <html.span style={styles.zoomValue}>
              {Math.round(shownScale * 100)}%
            </html.span>
          </html.div>
        </>
      )}
      {!src && !uploading && (
        <html.span
          data-testid="image-upload-prompt"
          style={styles.uploadPrompt}
        >
          Click to upload image
          <html.button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openMediaLibraryForPick({ gridId, itemId });
            }}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            style={styles.libraryLink}
          >
            or choose from library
          </html.button>
        </html.span>
      )}
      {uploading && <html.span style={styles.uploading}>Uploading…</html.span>}
      {backup === "backing-up" && !uploading && (
        <html.span style={styles.backupSlot}>
          <Tooltip.Root
            placement="bottom"
            content={
              roomPeers > 0
                ? "This image only exists on this device — keep this tab open until a collaborator loads it, then it's safe to close."
                : "This image only exists on this device. It backs up once another collaborator is online — keep this tab open."
            }
          >
            <Tooltip.Trigger>
              <html.span style={[styles.pill, styles.backupBadge]}>
                {roomPeers > 0
                  ? "⏳ Backing up — keep tab open"
                  : "⚠ Not backed up — no one else here"}
              </html.span>
            </Tooltip.Trigger>
          </Tooltip.Root>
        </html.span>
      )}
      {error && <html.span style={styles.error}>{error}</html.span>}
      {!cropping && (
        <html.span
          style={styles.pickerHidden}
          onClick={(e) => e.stopPropagation()}
        >
          {picker}
        </html.span>
      )}
    </html.div>
  );
}
